import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Loading from './components/common/Loading';
import Home from './pages/Home';
import AdminLogin from './pages/admin/AdminLogin';
import BillingLogin from './pages/billing/BillingLogin';
import InventoryLogin from './pages/inventory/InventoryLogin';
import AppsLogin from './pages/apps/AppsLogin';
import Inventory from './pages/admin/Inventory';
import Billing from './pages/admin/Billing';
import AdminDashboard from './pages/admin/Dashboard';
import BillingDashboard from './pages/billing/BillingDashboard';
import ReturnProduct from './pages/admin/ReturnProduct';
import InventoryDashboard from './pages/inventory/InventoryDashboard';
import AdminUsers from './pages/admin/Users';
import AdminAnalytics from './pages/admin/Analytics';
import SellHistory from './pages/admin/SellHistory';
import Companies from './pages/admin/Companies';
import Vendors from './pages/admin/Vendors';
import Quotations from './pages/admin/Quotations';
import Accounts from './pages/admin/Accounts';
import AccountsLedger from './pages/admin/AccountsLedger';
import AdminClientLedger from './pages/admin/ClientLedger';
import Customers from './pages/admin/Customers';
import ManufacturingCategories from './pages/admin/ManufacturingCategories';
import EcommerceAdmin from './pages/admin/EcommerceAdmin';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import { getDomainType, getDefaultRoute, shouldShowPortalSelection } from './utils/domainRouter';

// E-commerce imports
import ProductList from './pages/ecommerce/ProductList';
import ProductDetail from './pages/ecommerce/ProductDetail';
import Cart from './pages/ecommerce/Cart';
import Checkout from './pages/ecommerce/Checkout';
import Wishlist from './pages/ecommerce/Wishlist';
import Orders from './pages/ecommerce/Orders';
import OrderDetail from './pages/ecommerce/OrderDetail';
import Profile from './pages/ecommerce/Profile';
import Categories from './pages/ecommerce/Categories';
import UserBilling from './pages/ecommerce/UserBilling';
import BillingClientLedger from './pages/billing/ClientLedger';


// Welcome Page Component
const WelcomePage = () => {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif',
      color: '#fff'
    }}>
      <div style={{
        textAlign: 'center',
        maxWidth: '800px',
        padding: '2rem',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '20px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
      }}>
        <h1 style={{
          fontSize: '3.5rem',
          fontWeight: '800',
          marginBottom: '1.5rem',
          background: 'linear-gradient(45deg, #fff, #f0f9ff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Welcome to AVS Business Portal
        </h1>
        <p style={{
          fontSize: '1.25rem',
          marginBottom: '2rem',
          opacity: '0.9',
          lineHeight: '1.6'
        }}>
          Your comprehensive business management solution for billing, inventory, and administrative tasks.
        </p>
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginTop: '2rem'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '1rem 1.5rem',
            borderRadius: '10px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>Admin Portal</h3>
            <p style={{ margin: '0', fontSize: '0.9rem', opacity: '0.8' }}>Full system access</p>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '1rem 1.5rem',
            borderRadius: '10px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>Billing Portal</h3>
            <p style={{ margin: '0', fontSize: '0.9rem', opacity: '0.8' }}>Invoice management</p>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '1rem 1.5rem',
            borderRadius: '10px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.1rem' }}>Inventory Portal</h3>
            <p style={{ margin: '0', fontSize: '0.9rem', opacity: '0.8' }}>Stock management</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Domain-aware Root Redirect Component
const DomainAwareRedirect = () => {
  // Always show Home with login boxes on localhost and main domain
  // Login URLs will also redirect to Home component
  return <Home />;
};

// Admin Login Redirect Component
const AdminLoginRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loading />;
  }

  // If user is already an admin, redirect to dashboard
  if (user && user.role === 'admin') {
    return <Navigate to="/adminavsbusiness.com" replace />;
  }

  // Otherwise show Home with login boxes
  return <Home />;
};

// Protected Route Component (supports role lists)
const ProtectedRoute = ({ children, adminOnly = false, userOnly = false, allowRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <Loading />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  if (userOnly && user.role === 'admin') {
    return <Navigate to="/adminavsbusiness.com" replace />;
  }
  if (allowRoles && Array.isArray(allowRoles) && !allowRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Routes Component (minimal landing with only login boxes)
const AppRoutes = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow">
        <Routes>
          {/* Domain-aware root route */}
          <Route path="/" element={<DomainAwareRedirect />} />

          {/* Login pages redirect to Home with login boxes */}
          <Route path="/adminavsbusiness.com/login" element={<AdminLoginRedirect />} />
          <Route path="/apps.avsbusiness.com/login" element={<Home />} />
          <Route path="/apps.avsbusiness.com/inventory/login" element={<Home />} />
          <Route
            path="/adminavsbusiness.com"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/adminavsbusiness.com/sales/history" element={
            <ProtectedRoute adminOnly>
              <SellHistory />
            </ProtectedRoute>
          } />
          <Route path="/adminavsbusiness.com/users" element={
            <ProtectedRoute adminOnly>
              <AdminUsers />
            </ProtectedRoute>
          } />
          <Route path="/adminavsbusiness.com/analytics" element={
            <ProtectedRoute adminOnly>
              <AdminAnalytics />
            </ProtectedRoute>
          } />

          {/* Billing & Inventory & other admin resources */}
          <Route path="/adminavsbusiness.com/companies" element={
            <ProtectedRoute adminOnly>
              <Companies />
            </ProtectedRoute>
          } />
          <Route path="/adminavsbusiness.com/vendors" element={
            <ProtectedRoute adminOnly>
              <Vendors />
            </ProtectedRoute>
          } />
          <Route path="/adminavsbusiness.com/inventory" element={
            <ProtectedRoute adminOnly>
              <Inventory />
            </ProtectedRoute>
          } />
          <Route path="/adminavsbusiness.com/billing" element={
            <ProtectedRoute adminOnly>
              <Billing />
            </ProtectedRoute>
          } />
          <Route path="/adminavsbusiness.com/quotations" element={
            <ProtectedRoute adminOnly>
              <Quotations />
            </ProtectedRoute>
          } />
          <Route path="/adminavsbusiness.com/returns" element={
            <ProtectedRoute adminOnly>
              <ReturnProduct />
            </ProtectedRoute>
          } />
          <Route path="/adminavsbusiness.com/accounts" element={
            <ProtectedRoute adminOnly>
              <Accounts />
            </ProtectedRoute>
          } />
          <Route path="/adminavsbusiness.com/accounts-ledger" element={
            <ProtectedRoute adminOnly>
              <AccountsLedger />
            </ProtectedRoute>
          } />
          <Route path="/adminavsbusiness.com/client-ledger" element={
            <ProtectedRoute adminOnly>
              <AdminClientLedger />
            </ProtectedRoute>
          } />
          <Route path="/adminavsbusiness.com/customers" element={
            <ProtectedRoute adminOnly>
              <Customers />
            </ProtectedRoute>
          } />
          <Route path="/adminavsbusiness.com/manufacturing-categories" element={
            <ProtectedRoute adminOnly>
              <ManufacturingCategories />
            </ProtectedRoute>
          } />
          <Route path="/adminavsbusiness.com/ecommerce" element={
            <ProtectedRoute adminOnly>
              <EcommerceAdmin />
            </ProtectedRoute>
          } />

          {/* Inventory portal */}
          <Route
            path="/apps.avsbusiness.com/inventory/dashboard"
            element={
              <ProtectedRoute allowRoles={["admin", "inventory"]}>
                <InventoryDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/apps.avsbusiness.com/inventory"
            element={
              <ProtectedRoute allowRoles={["admin", "inventory"]}>
                <Inventory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/apps.avsbusiness.com/inventory/manufacturing-categories"
            element={
              <ProtectedRoute allowRoles={["admin", "inventory"]}>
                <ManufacturingCategories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/apps.avsbusiness.com/inventory/vendors"
            element={
              <ProtectedRoute allowRoles={["admin", "inventory"]}>
                <Vendors />
              </ProtectedRoute>
            }
          />

          {/* Billing portal */}
          <Route
            path="/apps.avsbusiness.com"
            element={
              <ProtectedRoute allowRoles={["admin", "billing"]}>
                <BillingDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/apps.avsbusiness.com/returns"
            element={
              <ProtectedRoute allowRoles={["admin", "billing"]}>
                <ReturnProduct />
              </ProtectedRoute>
            }
          />
          <Route
            path="/apps.avsbusiness.com/billing"
            element={
              <ProtectedRoute allowRoles={["admin", "billing"]}>
                <Billing />
              </ProtectedRoute>
            }
          />
          <Route
            path="/apps.avsbusiness.com/accounts-ledger"
            element={
              <ProtectedRoute allowRoles={["admin", "billing"]}>
                <AccountsLedger />
              </ProtectedRoute>
            }
          />
          <Route
            path="/apps.avsbusiness.com/client-ledger"
            element={
              <ProtectedRoute allowRoles={["admin", "billing"]}>
                <BillingClientLedger />
              </ProtectedRoute>
            }
          />

          {/* E-commerce Routes */}
          <Route path="/ecommerce" element={<ProductList />} />
          <Route path="/ecommerce/products/:slug" element={<ProductDetail />} />
          <Route path="/ecommerce/cart" element={<Cart />} />
          <Route path="/ecommerce/checkout" element={<Checkout />} />
          <Route path="/ecommerce/wishlist" element={<Wishlist />} />
          <Route path="/ecommerce/orders" element={<Orders />} />
          <Route path="/ecommerce/orders/:orderId" element={<OrderDetail />} />
          <Route path="/ecommerce/profile" element={<Profile />} />
          <Route path="/ecommerce/categories" element={<Categories />} />
          <Route path="/ecommerce/billing" element={<UserBilling />} />

          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
};

// Main App Component
const App = () => {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
};

export default App; 